import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TrailerPhotoCapture = ({ section, onPhotosChange, initialPhotos = [], isNA = false, onNAChange }) => {
  const [photos, setPhotos] = useState(initialPhotos);
  const [isDragging, setIsDragging] = useState(false);
  const [naChecked, setNaChecked] = useState(isNA);
  const fileInputRef = useRef(null);

  const handleNAChange = (e) => {
    const checked = e?.target?.checked;
    setNaChecked(checked);
    onNAChange?.(checked);
    if (checked) {
      // Clear photos when marking N/A
      setPhotos([]);
      onPhotosChange([]);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files || []);
    processFiles(files);
  };

  const processFiles = (files) => {
    const imageFiles = files?.filter(file => file?.type?.startsWith('image/'));
    const newPhotos = imageFiles?.map((file) => ({
      id: `${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`,
      file,
      preview: URL.createObjectURL(file),
      name: file?.name,
      timestamp: new Date()?.toISOString()
    }));
    const updatedPhotos = [...photos, ...newPhotos];
    setPhotos(updatedPhotos);
    onPhotosChange(updatedPhotos);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const files = Array.from(e?.dataTransfer?.files || []);
    processFiles(files);
  };

  const handleRemove = (photoId) => {
    const updatedPhotos = photos?.filter(photo => photo?.id !== photoId);
    setPhotos(updatedPhotos);
    onPhotosChange(updatedPhotos);
  };

  const isComplete = naChecked || photos?.length >= section?.requiredPhotos;
  const remainingPhotos = Math.max(0, section?.requiredPhotos - photos?.length);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isComplete ? 'bg-success text-success-foreground' : 'bg-primary/10 text-primary'
          }`}>
            <Icon name={naChecked ? 'MinusCircle' : section?.icon} size={20} strokeWidth={2} />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground">
              {section?.title}
            </h3>
            <p className="font-body text-sm text-muted-foreground">
              {section?.description}
            </p>
          </div>
        </div>
        <div className="text-right">
          {naChecked ? (
            <div className="font-heading text-sm font-semibold text-muted-foreground">
              N/A
            </div>
          ) : (
            <>
              <div className={`font-heading text-sm font-semibold ${
                isComplete ? 'text-success' : 'text-muted-foreground'
              }`}>
                {photos?.length} / {section?.requiredPhotos}
              </div>
              {!isComplete && (
                <p className="font-caption text-xs text-muted-foreground">
                  {remainingPhotos} more needed
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* N/A Checkbox */}
      <div className="flex items-center space-x-3 p-3 bg-muted/50 border border-border rounded-lg">
        <label className="flex items-center space-x-3 cursor-pointer flex-1">
          <div className="relative flex items-center">
            <input
              type="checkbox"
              checked={naChecked}
              onChange={handleNAChange}
              className="sr-only"
              id={`na-${section?.id}`}
            />
            <div
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all cursor-pointer ${
                naChecked
                  ? 'bg-[#FF6B35] border-[#FF6B35]'
                  : 'bg-background border-[#FF6B35]'
              }`}
              onClick={handleNAChange}
            >
              {naChecked && (
                <Icon name="Check" size={12} color="white" strokeWidth={3} />
              )}
            </div>
          </div>
          <div className="flex-1">
            <p className="font-body text-sm font-semibold text-foreground">
              N/A — Not applicable for this rental
            </p>
            <p className="font-caption text-xs text-muted-foreground">
              Check this if photos are not needed for this section
            </p>
          </div>
        </label>
      </div>

      {/* Photo upload area — hidden when N/A is checked */}
      {!naChecked && (
        <>
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-smooth ${
              isDragging
                ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
            }`}
          >
            <div className="flex flex-col items-center space-y-3">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="Camera" size={24} color="var(--color-primary)" strokeWidth={2} />
              </div>
              <div>
                <p className="font-body text-sm text-foreground mb-2">
                  Tap to capture or upload photos
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef?.current?.click()}
                  iconName="Upload"
                >
                  Select Photos
                </Button>
              </div>
              <p className="font-caption text-xs text-muted-foreground">
                JPG, PNG, WebP (Max 10MB per file)
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {photos?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {photos?.map((photo, index) => (
                <div key={photo?.id} className="relative group">
                  <div className="aspect-square rounded-lg overflow-hidden bg-muted border-2 border-border">
                    <img
                      src={photo?.preview}
                      alt={`${section?.title} photo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <button
                    onClick={() => handleRemove(photo?.id)}
                    className="absolute top-2 right-2 w-8 h-8 bg-error text-error-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-smooth hover:scale-110"
                    aria-label="Remove photo"
                  >
                    <Icon name="X" size={16} strokeWidth={2.5} />
                  </button>
                  <div className="absolute bottom-2 left-2 right-2 bg-background/80 backdrop-blur-sm rounded px-2 py-1">
                    <p className="font-caption text-xs text-foreground truncate">
                      Photo {index + 1}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {section?.tips && (
            <div className="p-3 bg-primary/5 border border-primary/20 rounded-md">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={16} className="text-primary mt-0.5" strokeWidth={2} />
                <div className="flex-1">
                  <p className="font-body text-xs text-foreground font-semibold mb-1">Tips:</p>
                  <ul className="font-body text-xs text-muted-foreground space-y-1 list-disc list-inside">
                    {section?.tips?.map((tip, index) => (
                      <li key={index}>{tip}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* N/A confirmation message */}
      {naChecked && (
        <div className="p-3 bg-muted/50 border border-border rounded-md flex items-center space-x-3">
          <Icon name="Info" size={16} className="text-muted-foreground" strokeWidth={2} />
          <p className="font-body text-sm text-muted-foreground">
            This section has been marked as not applicable. No photos required.
          </p>
        </div>
      )}
    </div>
  );
};

export default TrailerPhotoCapture;